import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { getAllRevenueCatProducts } from '../graphql/queries';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';

const PRODUCT_TYPES = [
  'WEEKLY',
  'MONTHLY', 
  'YEARLY',
  'INSTANT',
  'ONE_TIME',
  'PROMO'
];

const STORE_TYPES = [
  'APP_STORE',
  'GOOGLE_PLAY',
  'PROMOTIONAL'
];

export default function RevenueCatConfigPage() {
  const [config, setConfig] = useState({
    dailyCreditLimit: 10,
    productMappings: {},
    creditConfig: {}
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const { data: productsData, loading, refetch } = useQuery(getAllRevenueCatProducts, {
    variables: {
      pagination: { skip: 0, take: 1000 },
      filter: null
    }
  });

  useEffect(() => {
    // Initialize default configurations
    const defaultProductMappings = {
      WEEKLY: [],
      MONTHLY: [],
      YEARLY: [],
      INSTANT: [],
      ONE_TIME: [],
      PROMO: []
    };

    const defaultCreditConfig = {
      INITIAL_PURCHASE: {
        WEEKLY: 14,
        MONTHLY: 60,
        YEARLY: 720,
      },
      RENEWAL: {
        WEEKLY: 14,
        MONTHLY: 60,
        YEARLY: 720,
      },
      NON_RENEWING_PURCHASE: {
        INSTANT: 5,
        ONE_TIME: 10,
        PROMO: 15,
      },
    };

    setConfig({
      dailyCreditLimit: 10,
      productMappings: defaultProductMappings,
      creditConfig: defaultCreditConfig
    });
  }, []);

  const handleConfigChange = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const handleProductMappingChange = (type, index, value) => {
    setConfig(prev => ({
      ...prev,
      productMappings: {
        ...prev.productMappings,
        [type]: prev.productMappings[type].map((item, i) => 
          i === index ? value : item
        )
      }
    }));
  };

  const addProductMapping = (type) => {
    setConfig(prev => ({
      ...prev,
      productMappings: {
        ...prev.productMappings,
        [type]: [...(prev.productMappings[type] || []), '']
      }
    }));
  };

  const removeProductMapping = (type, index) => {
    setConfig(prev => ({
      ...prev,
      productMappings: {
        ...prev.productMappings,
        [type]: prev.productMappings[type].filter((_, i) => i !== index)
      }
    }));
  };

  const handleSave = () => {
    // Here you would typically save to backend or localStorage
    setSnackbar({
      open: true,
      message: 'Configuration saved successfully!',
      severity: 'success'
    });
    
    // For now, save to localStorage
    localStorage.setItem('revenueCatConfig', JSON.stringify(config));
  };

  const handleRefresh = () => {
    refetch();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully!',
      severity: 'info'
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const products = productsData?.getAllCreditProducts?.items || [];

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 4 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MonetizationOnIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            RevenueCat Configuration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save Configuration
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Daily Credit Limit */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Daily Credit Limit
              </Typography>
              <TextField
                fullWidth
                label="Daily Credit Limit"
                type="number"
                value={config.dailyCreditLimit}
                onChange={(e) => setConfig(prev => ({ ...prev, dailyCreditLimit: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 1, step: 1 }}
                helperText="Maximum credits a user can use per day"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Product Mappings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Product Mappings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Map product IDs to product types for automatic categorization
              </Typography>
              
              <Grid container spacing={2}>
                {PRODUCT_TYPES.map((type) => (
                  <Grid item xs={12} md={6} key={type}>
                    <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {type}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addProductMapping(type)}
                        >
                          Add
                        </Button>
                      </Box>
                      
                      {config.productMappings[type]?.map((productId, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Product ID"
                            value={productId}
                            onChange={(e) => handleProductMappingChange(type, index, e.target.value)}
                            sx={{ flexGrow: 1 }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeProductMapping(type, index)}
                          >
                            ×
                          </IconButton>
                        </Box>
                      ))}
                      
                      {(!config.productMappings[type] || config.productMappings[type].length === 0) && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No products mapped
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Credit Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Credit Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure credit amounts for different purchase types and product categories
              </Typography>
              
              <Grid container spacing={3}>
                {Object.entries(config.creditConfig).map(([purchaseType, typeConfig]) => (
                  <Grid item xs={12} md={6} key={purchaseType}>
                    <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {purchaseType.replace(/_/g, ' ')}
                      </Typography>
                      
                      {Object.entries(typeConfig).map(([productType, amount]) => (
                        <Box key={productType} sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ minWidth: 80 }}>
                            {productType}:
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={amount}
                            onChange={(e) => handleConfigChange('creditConfig', purchaseType, {
                              ...config.creditConfig[purchaseType],
                              [productType]: parseInt(e.target.value) || 0
                            })}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 100 }}
                          />
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Current Products Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current Products
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Active RevenueCat products in the system
              </Typography>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Price</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="medium">
                            {product.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={product.type}
                            color="primary"
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {product.amount} credits
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ${product.price}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={new Date(product.endDate) > new Date() ? 'Active' : 'Expired'}
                            color={new Date(product.endDate) > new Date() ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
